import React, { Fragment, useEffect, useRef, useState } from 'react';
import { 
  Search, Languages, Sun, Star, Bell, 
  LayoutDashboard, User, ChevronDown, Menu as MenuIcon, X, Sparkles, Database
} from 'lucide-react';
import { Menu, Transition, Dialog } from '@headlessui/react';
import { useDataServiceAccess } from '../../Admin/Components/DataService/useDataServiceAccess';
import { useRedirect } from '../../Shared/Hooks/useRedirect ';
import ProfileDropdown from '../../App/Components/ProfileDropdown';
import { CandidateProfile } from '../Types/CandidateType';
import { useNotificationContext } from '../../Shared/Context/NotificationContext';
import { UserForm } from '../../App/Types/UsersType';
import { MultiThemeLayout } from '../../Routes/MultiThemeLayout';
import { markNotificationAsRead } from '../../Notification/Service/NotificationService';
import { formatDateTime } from '../../Shared/Utils/HelperUtils';

// 🔹 Explicitly type the items inside the navigation array to satisfy TypeScript compiler
interface ChildNavItem {
  label: string;
  path: string;
}

interface NavigationItem {
  icon: React.ComponentType<any>;
  label: string;
  path?: string;
  active: boolean;
  children?: ChildNavItem[];
}


interface CandidateDashboardHeaderProps {
  candidate: UserForm | null;
}

// Updated NavItem to handle Dropdowns beautifully
const NavItem = ({
  icon: Icon,
  label,
  path,
  active = false,
  children,
}: any) => {
  const redirect = useRedirect();

  // 🔹 If NO children → Simple redirect item
  if (!children) {
    return (
      <div
        onClick={() => path && redirect(path)}
        className={`flex items-center gap-2 px-4 py-2.5 rounded-xl cursor-pointer transition-all duration-200 whitespace-nowrap active:scale-95 ${
          active
            ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white shadow-lg shadow-purple-500/20'
            : 'text-indigo-100 hover:bg-white/10 hover:text-white'
        }`}
      >
        <Icon size={18} />
        <span className="text-sm font-semibold tracking-wide">{label}</span>
      </div>
    );
  }

  // 🔹 If has children → Dropdown (Desktop)
  return (
    <Menu as="div" className="relative">
      <Menu.Button
        className={`flex items-center gap-2 px-4 py-2.5 rounded-xl cursor-pointer transition-all duration-200 whitespace-nowrap active:scale-95 ${
          active
            ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white shadow-lg shadow-purple-500/20'
            : 'text-indigo-100 hover:bg-white/10 hover:text-white'
        }`}
      >
        <Icon size={18} />
        <span className="text-sm font-semibold tracking-wide">{label}</span>
        <ChevronDown size={14} className="ml-1 opacity-70 group-hover:rotate-180 transition-transform" />
      </Menu.Button>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-150"
        enterFrom="transform opacity-0 scale-95 translate-y-1"
        enterTo="transform opacity-100 scale-100 translate-y-0"
        leave="transition ease-in duration-100"
        leaveFrom="transform opacity-100 scale-100 translate-y-0"
        leaveTo="transform opacity-0 scale-95 translate-y-1"
      >
        <Menu.Items className="absolute left-0 mt-2 w-52 origin-top-left rounded-xl bg-slate-900 border border-white/10 p-1.5 shadow-2xl focus:outline-none z-[60]">
          {children.map((item: any, idx: number) => (
            <Menu.Item key={idx}>
              {({ active }) => (
                <button
                  onClick={() => redirect(item.path)}
                  className={`${
                    active
                      ? 'bg-gradient-to-r from-pink-500/20 to-purple-500/20 text-pink-300 border-l-2 border-pink-500'
                      : 'text-gray-300 hover:text-white'
                  } group flex w-full items-center rounded-lg px-4 py-2.5 text-sm font-medium transition-all`}
                >
                  {item.label}
                </button>
              )}
            </Menu.Item>
          ))}
        </Menu.Items>
      </Transition>
    </Menu>
  );
};

// function CandidateHeaderComponent() {
  function CandidateHeaderComponent({
    candidate,
  }: CandidateDashboardHeaderProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState<string | null>(null);
  const redirect = useRedirect();

  // A candidate is granted access individually, so the link appears only once
  // an admin has actually configured one.
  const { hasDataServiceAccess } = useDataServiceAccess();

const [showNotifications, setShowNotifications] = useState(false);

const { notifications } = useNotificationContext();


useEffect(() => {
  console.log("Notifications:", notifications);
}, [notifications]);

const unreadCount = notifications.filter(
  notification => !notification.read
).length;

// 🔹 CLICK OUTSIDE FIX HOOKS
const notificationRef = useRef<HTMLDivElement>(null);
useEffect(() => {
  const handleClickOutside = (event: MouseEvent) => {
    if (
      notificationRef.current && 
      !notificationRef.current.contains(event.target as Node)
    ) {
      setShowNotifications(false);
    }
  };

  if (showNotifications) {
    document.addEventListener('mousedown', handleClickOutside);
  }
  return () => {
    document.removeEventListener('mousedown', handleClickOutside);
  };
}, [showNotifications]);


const handleNotificationClick = async (id: string) => {
  try {
    await markNotificationAsRead(id);
    // redirect("/invitations/pending")
  } catch (error) {
    console.error("Failed to mark notification as read", error);
  }
};





  // Navigation schema bound to the NavigationItem definition to stop errors downstream
  const navigationConfig: NavigationItem[] = [
    {
      icon: LayoutDashboard,
      label: "Dashboards",
      path: "/candidate/dashboard",
      active: true,
    },
    // {
    //   icon: Sparkles,
    //   label: "Command Center",
    //   path: "/candidate/command-center",
    //   active: false
    // },
    {
      icon: User,
      label: "Invite Candidates",
      path: "/candidate/invite-link",
      active: false
    },
    {
      icon: User,
      label: "Feedback",
      path: "/candidate/feedback",
      active: false
    },
    ...(hasDataServiceAccess
      ? [
          {
            icon: Database,
            label: "Data Service",
            path: "/candidate/data-service",
            active: false,
          } as NavigationItem,
        ]
      : []),
  ];

  const toggleMobileDropdown = (label: string) => {
    setMobileDropdownOpen(mobileDropdownOpen === label ? null : label);
  };

  return (
    <div>
      {/* HEADER WRAPPER WITH NEXT-GEN GRADIENT */}
      <header className="bg-gradient-to-r from-slate-950 via-indigo-950 to-slate-950 px-4 md:px-8 py-4 flex flex-col gap-4 border-b border-white/10 sticky top-0 z-50 shadow-xl backdrop-blur-md bg-opacity-95">
        
        {/* TOP BAR */}
        <div className="flex justify-between items-center">
          
          {/* LOGO AREA */}
          <div 
            onClick={() => window.location.href = '/candidate/dashboard'} 
            className="flex items-center gap-3 cursor-pointer group"
          >
            <div className="w-9 h-9 bg-gradient-to-tr from-pink-500 to-yellow-400 rounded-xl flex items-center justify-center shadow-lg shadow-pink-500/20 group-hover:rotate-6 transition-transform">
              <span className="text-slate-950 font-black text-lg">J</span>
            </div>
            <h1 className="text-xl font-extrabold tracking-wider bg-gradient-to-r from-white via-indigo-200 to-pink-300 bg-clip-text text-transparent uppercase">
              Nexgent
            </h1>
          </div>
          
          {/* UTILITY ACTIONS */}
          <div className="flex items-center gap-3 md:gap-5 text-indigo-200">
            {/* <Search size={19} className="hidden sm:block cursor-pointer hover:text-pink-400 transition-colors" />
            <Languages size={19} className="hidden sm:block cursor-pointer hover:text-pink-400 transition-colors" />
            <Sun size={19} className="cursor-pointer hover:text-yellow-300 transition-colors" />
            <Star size={19} className="hidden sm:block cursor-pointer hover:text-yellow-300 transition-colors" /> */}


            
            
            {/* <div className="relative cursor-pointer hover:text-white transition-colors">
              <Bell size={19} />
              <span className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-gradient-to-r from-red-500 to-pink-500 text-white text-[9px] font-bold flex items-center justify-center rounded-full ring-2 ring-slate-950">
                4
              </span>
            </div> */}

<div className="relative" ref={notificationRef}>

<div
  onClick={() =>
    setShowNotifications(!showNotifications)
  }
  className="relative cursor-pointer hover:text-white transition-colors"
>
  <Bell size={19} />

  {unreadCount > 0 && (
    <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 bg-gradient-to-r from-red-500 to-pink-500 text-white text-[9px] font-bold flex items-center justify-center rounded-full ring-2 ring-slate-950">
      {unreadCount > 99 ? "99+" : unreadCount}
    </span>
  )}
</div>

{showNotifications && (
  <div
    className="
      fixed sm:absolute
      top-16 sm:top-8
      left-3 right-3 sm:left-auto sm:right-0
      w-auto sm:w-96
      max-h-[75vh] sm:max-h-[500px]
      overflow-y-auto
      rounded-xl
      bg-slate-900
      border border-white/10
      shadow-2xl
      z-[999]
    "
  >
    <div className="sticky top-0 bg-slate-900 border-b border-white/10 p-4">
      <h3 className="text-white font-semibold">
        Notifications
      </h3>
    </div>

    {notifications.length === 0 ? (
      <div className="p-6 text-center text-gray-400">
        No notifications available
      </div>
    ) : (
      notifications.map((notification: any) => (
        <div
          key={notification.id}
          onClick={() => handleNotificationClick(notification.id)}
          className={`p-4 border-b border-white/5 hover:bg-white/5 cursor-pointer ${
            !notification.read
              ? "bg-indigo-500/10"
              : ""
          }`}
        >
          <div className="text-white font-medium text-sm sm:text-base">
            {notification.title}
          </div>

          {/* <div className="text-xs sm:text-sm text-gray-400 mt-1 break-words">
            {notification.message}
          </div> */}

<div className="text-xs sm:text-sm text-gray-400 mt-1 break-words">
  {notification.message}

  <span
    className={`ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium border ${
      notification.isRead
        ? "bg-green-500/10 text-green-400 border-green-500/20"
        : "bg-amber-500/10 text-amber-400 border-amber-500/20"
    }`}
  >
    {notification.isRead ? "Read" : "Unread"}
  </span>
</div>

          <div className="text-[10px] sm:text-xs text-gray-500 mt-2">
            { formatDateTime(notification.createdAt)}
          </div>
        </div>
      ))
    )}
  </div>
)}

</div>






            {/* <img 
              src="https://i.pravatar.cc/150?u=norris" 
              className="w-9 h-9 rounded-xl border-2 border-white/20 shadow-xl cursor-pointer hover:border-pink-500 transition-all" 
              alt="Profile" 
            /> */}

            <ProfileDropdown candidate={candidate}/>

            {/* MOBILE HAMBURGER TOGGLE */}
            <button 
              onClick={() => setMobileMenuOpen(true)}
              className="md:hidden p-1.5 rounded-lg text-indigo-200 hover:bg-white/10 hover:text-white transition-colors"
            >
              <MenuIcon size={24} />
            </button>
          </div>
        </div>

        {/* DESKTOP HORIZONTAL NAVIGATION */}
        <div className="hidden md:block overflow-x-auto no-scrollbar">
          <nav className="flex items-center gap-2 pb-1 whitespace-nowrap">
            {navigationConfig.map((item, index) => (
              <NavItem 
                key={index}
                icon={item.icon}
                label={item.label}
                path={item.path}
                active={item.active}
                children={item.children}
              />
            ))}
          </nav>
        </div>
      </header>

      {/* MOBILE SIDEBAR SLIDEOUT DRAWER (FULLY RESPONSIVE) */}
      <Transition.Root show={mobileMenuOpen} as={Fragment}>
        <Dialog as="div" className="relative z-[100] md:hidden" onClose={setMobileMenuOpen}>
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 flex justify-end">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <Dialog.Panel className="relative flex w-full max-w-xs flex-col overflow-y-auto bg-gradient-to-b from-slate-900 to-indigo-950 p-6 shadow-2xl border-l border-white/10">
                {/* CLOSE MENU HEADER */}
                <div className="flex items-center justify-between mb-8">
                  <h2 className="text-lg font-bold text-white tracking-wide">Navigation</h2>
                  <button
                    type="button"
                    className="p-2 rounded-xl text-indigo-200 hover:bg-white/10 hover:text-white transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <X size={20} />
                  </button>
                </div>

                {/* MOBILE NAV ITEMS LIST */}
                <nav className="flex flex-col gap-2">
                  {navigationConfig.map((item, idx) => {
                    const Icon = item.icon;
                    const hasChildren = !!item.children;
                    const isOpen = mobileDropdownOpen === item.label;

                    return (
                      <div key={idx} className="w-full">
                        {hasChildren ? (
                          <>
                            {/* Expandable parent trigger */}
                            <button
                              onClick={() => toggleMobileDropdown(item.label)}
                              className={`w-full flex items-center justify-between px-4 py-3 rounded-xl font-medium transition-all ${
                                item.active 
                                  ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white' 
                                  : 'text-indigo-100 hover:bg-white/5'
                              }`}
                            >
                              <div className="flex items-center gap-3">
                                <Icon size={20} />
                                <span className="text-sm font-semibold">{item.label}</span>
                              </div>
                              <ChevronDown size={16} className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
                            </button>

                            {/* Dropdown Items nested smoothly inside drawer */}
                            {isOpen && (
                              <div className="mt-1 ml-4 pl-4 border-l border-white/10 flex flex-col gap-1">
                                {item.children?.map((subItem: any, subIdx: number) => (
                                  <button
                                    key={subIdx}
                                    onClick={() => {
                                      redirect(subItem.path);
                                      setMobileMenuOpen(false);
                                    }}
                                    className="w-full text-left px-3 py-2 text-sm text-indigo-200 hover:text-white rounded-lg hover:bg-white/5 transition-colors"
                                  >
                                    {subItem.label}
                                  </button>
                                ))}
                              </div>
                            )}
                          </>
                        ) : (
                          /* Simple route action button */
                          <button
                            onClick={() => {
                              if (item.path) redirect(item.path);
                              setMobileMenuOpen(false);
                            }}
                            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all text-left ${
                              item.active 
                                ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white shadow-lg' 
                                : 'text-indigo-100 hover:bg-white/5'
                            }`}
                          >
                            <Icon size={20} />
                            <span className="text-sm font-semibold">{item.label}</span>
                          </button>
                        )}
                      </div>
                    );
                  })}
                </nav>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>
    </div>
  );
}

export default CandidateHeaderComponent;